import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "section" }
const _hoisted_2 = { class: "chart-wrapper" }
const _hoisted_3 = { class: "rate-grid" }
const _hoisted_4 = { class: "subject-name" }
const _hoisted_5 = { class: "rate-value" }
const _hoisted_6 = {
  key: 0,
  class: "rate-grid"
}
const _hoisted_7 = { class: "subject-name" }
const _hoisted_8 = { class: "rate-value" }
const _hoisted_9 = {
  key: 1,
  class: "no-data-tip"
}
const _hoisted_10 = {
  key: 0,
  class: "rate-grid"
}
const _hoisted_11 = { class: "subject-name" }
const _hoisted_12 = { class: "rate-value" }
const _hoisted_13 = {
  key: 1,
  class: "no-data-tip"
}
const _hoisted_14 = {
  key: 0,
  class: "rate-grid"
}
const _hoisted_15 = { class: "subject-name" }
const _hoisted_16 = { class: "rate-value" }
const _hoisted_17 = {
  key: 1,
  class: "no-data-tip"
}

import { computed, ref } from 'vue';

// 定义显示项的接口
interface RateItem {
  subject: string;
  subjectName: string;
  rate: number;
}

// 定义班级信息接口
interface ClassInfo {
  id: number;
  name: string;
  header: number;
  school_id: number;
  subject_selection: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PassLineRate',
  props: {
    passLineRateData: {},
    classInfo: {}
  },
  setup(__props: any) {

const props = __props;

// 激活的tab
const activeTab = ref('pass_rate');

// 科目名称映射（支持拼音键和中文键，统一转为中文显示）
const subjectMap: Record<string, string> = {
  // 拼音键 → 中文
  'yuwen': '语文',
  'shuxue': '数学',
  'yingyu': '英语',
  'wuli': '物理',
  'huaxue': '化学',
  'shengwu': '生物',
  'lishi': '历史',
  'zhengzhi': '政治',
  'dili': '地理',
  'sum_': '总分',
  // 中文键 → 中文（兼容已有数据）
  '语文': '语文',
  '数学': '数学',
  '英语': '英语',
  '化学': '化学',
  '物理': '物理',
  '政治': '政治',
  '地理': '地理',
  '生物': '生物',
  '历史': '历史',
  '总分': '总分'
};

// 获取科目名称
const getSubjectName = (subject: string) => {
  return subjectMap[subject] || subject;
};

// 根据选科信息判断科目是否应该显示
const shouldShowSubject = (subjectKey: string): boolean => {
  // 如果没有班级信息或没有选科信息，则显示所有科目
  if (!props.classInfo || !props.classInfo.subject_selection) {
    return true;
  }

  // 先将科目键转为中文名，再根据中文名判断
  const cnName = getSubjectName(subjectKey);
  switch (cnName) {
    case '物理':
      return props.classInfo.subject_selection.includes('物') || props.classInfo.subject_selection.includes('理');
    case '化学':
      return props.classInfo.subject_selection.includes('化');
    case '生物':
      return props.classInfo.subject_selection.includes('生');
    case '历史':
      return props.classInfo.subject_selection.includes('史');
    case '政治':
      return props.classInfo.subject_selection.includes('政');
    case '地理':
      return props.classInfo.subject_selection.includes('地');
    default:
      // 语数英等主科 always 显示
      return true;
  }
};

// 处理数据，确保我们获取的是正确的数据结构
const processedData = computed(() => {
  if (!props.passLineRateData || !Array.isArray(props.passLineRateData) || props.passLineRateData.length === 0) {
    return null;
  }
  
  // 假设我们只需要第一个班级的数据
  return props.passLineRateData[0];
});

// 各种过线率数据
const passRateData = computed(() => {
  return processedData.value?.pass_rate || {};
});

const topRateData = computed(() => {
  return processedData.value?.top_rate || {};
});

const top9RateData = computed(() => {
  return processedData.value?.top9_rate || {};
});

const top2RateData = computed(() => {
  return processedData.value?.top2_rate || {};
});

// 将数据转换为显示项数组,并根据选科信息过滤
const passRateItems = computed(() => {
  return Object.keys(passRateData.value)
    .filter(key => passRateData.value[key] !== undefined && shouldShowSubject(key))
    .map(key => ({
      subject: key,
      subjectName: getSubjectName(key),
      rate: passRateData.value[key]
    }));
});

const topRateItems = computed(() => {
  return Object.keys(topRateData.value)
    .filter(key => topRateData.value[key] !== undefined && shouldShowSubject(key))
    .map(key => ({
      subject: key,
      subjectName: getSubjectName(key),
      rate: topRateData.value[key]
    }));
});

const top9RateItems = computed(() => {
  return Object.keys(top9RateData.value)
    .filter(key => top9RateData.value[key] !== undefined && shouldShowSubject(key))
    .map(key => ({
      subject: key,
      subjectName: getSubjectName(key),
      rate: top9RateData.value[key]
    }));
});

const top2RateItems = computed(() => {
  return Object.keys(top2RateData.value)
    .filter(key => top2RateData.value[key] !== undefined && shouldShowSubject(key))
    .map(key => ({
      subject: key,
      subjectName: getSubjectName(key),
      rate: top2RateData.value[key]
    }));
});

// 折线图配置
const passLineChartOption = computed(() => {
  // 获取所有科目
  const subjects = passRateItems.value.map(item => item.subjectName);
  
  // 获取各线的数据
  const passRate = passRateItems.value.map(item => item.rate);
  const topRate = topRateItems.value.map(item => item.rate);
  const top9Rate = top9RateItems.value.map(item => item.rate);
  const top2Rate = top2RateItems.value.map(item => item.rate);

  return {
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: ['特控率', '清北率', '985率', '211率']
    },
    xAxis: {
      type: 'category',
      data: subjects
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value}%'
      }
    },
    series: [
      {
        name: '特控率',
        type: 'line',
        data: passRate,
        label: {
          show: true,
          position: 'top',
          formatter: '{c}%'
        }
      },
      {
        name: '清北率',
        type: 'line',
        data: topRate,
        label: {
          show: true,
          position: 'top',
          formatter: '{c}%'
        }
      },
      {
        name: '985率',
        type: 'line',
        data: top9Rate,
        label: {
          show: true,
          position: 'top',
          formatter: '{c}%'
        }
      },
      {
        name: '211率',
        type: 'line',
        data: top2Rate,
        label: {
          show: true,
          position: 'top',
          formatter: '{c}%'
        }
      }
    ]
  };
});

return (_ctx: any,_cache: any) => {
  const _component_e_charts = _resolveComponent("e-charts")!
  const _component_a_card = _resolveComponent("a-card")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_tab_pane = _resolveComponent("a-tab-pane")!
  const _component_a_tabs = _resolveComponent("a-tabs")!
  const _component_a_row = _resolveComponent("a-row")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "section-title" }, "分数线过线率", -1)),
    _createVNode(_component_a_row, { gutter: 16 }, {
      default: _withCtx(() => [
        _createVNode(_component_a_col, {
          xs: 24,
          md: 16
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_card, {
              class: "chart-card",
              title: "各科目过线率折线图"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_e_charts, {
                    option: passLineChartOption.value,
                    autoresize: "",
                    style: {"height":"360px","width":"100%"}
                  }, null, 8, ["option"])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_a_col, {
          xs: 24,
          md: 8
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_card, {
              class: "stat-card",
              title: "各科目过线率详情"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_tabs, {
                  activeKey: activeTab.value,
                  "onUpdate:activeKey": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_tab_pane, {
                      key: "pass_rate",
                      tab: "特控率"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_3, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(passRateItems.value, (item) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: item.subject,
                              class: "rate-item"
                            }, [
                              _createElementVNode("span", _hoisted_4, _toDisplayString(item.subjectName), 1),
                              _createElementVNode("span", _hoisted_5, _toDisplayString(item.rate) + "%", 1)
                            ]))
                          }), 128))
                        ])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_tab_pane, {
                      key: "top_rate",
                      tab: "清北率"
                    }, {
                      default: _withCtx(() => [
                        (topRateItems.value.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(topRateItems.value, (item) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: item.subject,
                                  class: "rate-item"
                                }, [
                                  _createElementVNode("span", _hoisted_7, _toDisplayString(item.subjectName), 1),
                                  _createElementVNode("span", _hoisted_8, _toDisplayString(item.rate) + "%", 1)
                                ]))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_9, "未配置清北线"))
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_tab_pane, {
                      key: "top9_rate",
                      tab: "985率"
                    }, {
                      default: _withCtx(() => [
                        (top9RateItems.value.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(top9RateItems.value, (item) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: item.subject,
                                  class: "rate-item"
                                }, [
                                  _createElementVNode("span", _hoisted_11, _toDisplayString(item.subjectName), 1),
                                  _createElementVNode("span", _hoisted_12, _toDisplayString(item.rate) + "%", 1)
                                ]))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_13, "未配置985线"))
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_tab_pane, {
                      key: "top2_rate",
                      tab: "211率"
                    }, {
                      default: _withCtx(() => [
                        (top2RateItems.value.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(top2RateItems.value, (item) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: item.subject,
                                  class: "rate-item"
                                }, [
                                  _createElementVNode("span", _hoisted_15, _toDisplayString(item.subjectName), 1),
                                  _createElementVNode("span", _hoisted_16, _toDisplayString(item.rate) + "%", 1)
                                ]))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_17, "未配置211线"))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["activeKey"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})